/**
 * ProductPickerModal — browse/create products or apps.
 * URL scrape via /api/ugc-studio/products/scrape-and-save, or manual form.
 */
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { Loader2, Link2, Trash2, Upload, X, Maximize2 } from "lucide-react";
import { getAuthToken } from "@/lib/queryClient";
import { ugcStudioApi, type Platform, type ProductType, type UgcProduct } from "./api";
import { MediaGridSkeleton } from "@/components/skeletons";

interface ProductPickerModalProps {
  open: boolean;
  onClose: () => void;
  type: ProductType;
  selectedId: string | null;
  onSelect: (product: UgcProduct | null) => void;
}

export function ProductPickerModal({
  open,
  onClose,
  type,
  selectedId,
  onSelect,
}: ProductPickerModalProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const qc = useQueryClient();
  const [url, setUrl] = useState("");
  const [platform, setPlatform] = useState<Platform>("desktop");
  const [manualMode, setManualMode] = useState(false);
  const [manualName, setManualName] = useState("");
  const [manualDesc, setManualDesc] = useState("");
  const [manualFiles, setManualFiles] = useState<File[]>([]);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const manualFileInputRef = useRef<HTMLInputElement>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["ugc-studio", "products", type],
    queryFn: () => ugcStudioApi.listProducts(type),
    enabled: open,
  });

  const scrapeMut = useMutation({
    mutationFn: () =>
      ugcStudioApi.scrapeAndSaveProduct(url, type, type === "app" ? platform : null),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["ugc-studio", "products", type] });
      onSelect(res.product);
      setUrl("");
      onClose();
    },
    onError: (err: Error) =>
      toast({
        title: t("ugcStudio.product.fetchFailed"),
        description: err.message,
        variant: "destructive",
      }),
  });

  const createMut = useMutation({
    mutationFn: async () => {
      const res = await ugcStudioApi.createProduct({
        type,
        name: manualName,
        description: manualDesc,
      });
      if (manualFiles.length > 0) {
        const uploaded = await ugcStudioApi.uploadProductImages({
          productId: res.product.id,
          files: manualFiles,
          token: getAuthToken() ?? "",
        });
        return uploaded;
      }
      return res;
    },
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["ugc-studio", "products", type] });
      onSelect(res.product);
      setManualMode(false);
      setManualName("");
      setManualDesc("");
      setManualFiles([]);
      onClose();
    },
    onError: (err: Error) =>
      toast({
        title: t("ugcStudio.product.saveFailed"),
        description: err.message,
        variant: "destructive",
      }),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => ugcStudioApi.deleteProduct(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["ugc-studio", "products", type] }),
  });

  const title = type === "app" ? t("ugcStudio.product.addApp") : t("ugcStudio.product.addProduct");
  const subtitle =
    type === "app" ? t("ugcStudio.product.appSubtitle") : t("ugcStudio.product.productSubtitle");
  const placeholder = type === "app" ? "www.yourapp.com" : "www.yourproduct.com";
  const products = data?.products ?? [];

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
    >
      <DialogContent className="bg-[#0a0c10] border-white/[0.08] max-w-3xl">
        <div className="mb-4 pr-8">
          <h2 className="text-white text-xl font-semibold tracking-wide">{title}</h2>
          <p className="text-white/50 text-sm mt-1 max-w-md">{subtitle}</p>
        </div>

        {!manualMode && (
          <div className="space-y-3 mb-4">
            {type === "app" && (
              <div className="flex items-center gap-2">
                <span className="text-white/40 text-xs uppercase tracking-wider mr-1">
                  {t("ugcStudio.product.deviceLabel")}
                </span>
                <PlatformPill
                  active={platform === "desktop"}
                  onClick={() => setPlatform("desktop")}
                  label={t("ugcStudio.product.deviceDesktop")}
                />
                <PlatformPill
                  active={platform === "mobile"}
                  onClick={() => setPlatform("mobile")}
                  label={t("ugcStudio.product.deviceMobile")}
                />
              </div>
            )}
            <div className="flex items-center gap-3">
              <div className="flex-1 relative">
                <Link2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                <Input
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder={placeholder}
                  className="pl-9 bg-white/[0.04] border-white/[0.08] text-white"
                />
              </div>
              <Button
                onClick={() => scrapeMut.mutate()}
                disabled={!url || scrapeMut.isPending}
                className="bg-lime-400 hover:bg-lime-300 text-black"
              >
                {scrapeMut.isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  t("ugcStudio.product.fetchButton")
                )}
              </Button>
              <span className="text-white/40 text-xs">{t("ugcStudio.product.orText")}</span>
              <Button
                variant="outline"
                onClick={() => setManualMode(true)}
                className="border-white/15 text-white/80"
              >
                {t("ugcStudio.product.manualButton")}
              </Button>
            </div>
          </div>
        )}

        {manualMode && (
          <div className="space-y-3 mb-4 p-4 rounded-xl bg-white/[0.02] border border-white/[0.06]">
            <Input
              value={manualName}
              onChange={(e) => setManualName(e.target.value)}
              placeholder="Nazwa"
              className="bg-white/[0.04] border-white/[0.08] text-white"
            />
            <Textarea
              value={manualDesc}
              onChange={(e) => setManualDesc(e.target.value)}
              placeholder="Opis"
              className="bg-white/[0.04] border-white/[0.08] text-white min-h-20"
            />

            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-white/50 text-xs uppercase tracking-wider">
                  {t("ugcStudio.product.imagesLabel")}
                </span>
                <span className="text-white/30 text-[10px]">
                  {t("ugcStudio.product.imagesHint")}
                </span>
              </div>
              <input
                ref={manualFileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                multiple
                className="hidden"
                onChange={(e) => {
                  const chosen = Array.from(e.target.files ?? []).slice(0, 8);
                  setManualFiles(chosen);
                }}
              />
              {manualFiles.length === 0 ? (
                <button
                  type="button"
                  onClick={() => manualFileInputRef.current?.click()}
                  className="w-full flex items-center justify-center gap-2 py-6 rounded-lg border border-dashed border-white/[0.12] bg-white/[0.02] text-white/60 hover:border-white/25 hover:bg-white/[0.04] transition"
                >
                  <Upload className="w-4 h-4" />
                  <span className="text-sm">{t("ugcStudio.product.uploadImages")}</span>
                </button>
              ) : (
                <div className="space-y-2">
                  <div className="grid grid-cols-4 gap-2">
                    {manualFiles.map((f, idx) => (
                      <div
                        key={idx}
                        className="relative aspect-square rounded-md overflow-hidden border border-white/[0.08] bg-white/[0.03]"
                      >
                        <img
                          src={URL.createObjectURL(f)}
                          alt={f.name}
                          className="w-full h-full object-cover"
                        />
                        <button
                          type="button"
                          onClick={() => setManualFiles(manualFiles.filter((_, i) => i !== idx))}
                          className="absolute top-1 right-1 p-1 rounded-md bg-black/70 text-white/80 hover:text-white"
                          aria-label={t("ugcStudio.product.deleteImage")}
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                  <button
                    type="button"
                    onClick={() => manualFileInputRef.current?.click()}
                    className="text-white/60 hover:text-white text-xs underline"
                  >
                    {t("ugcStudio.product.changeSelection")}
                  </button>
                </div>
              )}
            </div>

            <div className="flex gap-2">
              <Button
                onClick={() => createMut.mutate()}
                disabled={!manualName || createMut.isPending}
                className="bg-lime-400 hover:bg-lime-300 text-black"
              >
                {createMut.isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  t("ugcStudio.product.saveButton")
                )}
              </Button>
              <Button
                variant="ghost"
                onClick={() => {
                  setManualMode(false);
                  setManualFiles([]);
                }}
                className="text-white/60"
              >
                {t("ugcStudio.product.cancelButton")}
              </Button>
            </div>
          </div>
        )}

        <div className="rounded-xl bg-white/[0.02] border border-white/[0.06] min-h-[240px] p-4">
          {isLoading ? (
            // VP-208: siatka produktów zamiast spinnera — kontener ma min-h-[240px],
            // więc placeholder wypełnia go zamiast zostawiać pustkę.
            <MediaGridSkeleton
              count={6}
              withFooter={false}
              gridClassName="grid grid-cols-2 md:grid-cols-3 gap-3"
            />
          ) : products.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 text-center gap-2">
              <div className="text-white font-semibold">
                {type === "app" ? t("ugcStudio.product.noApp") : t("ugcStudio.product.noProduct")}
              </div>
              <div className="text-white/40 text-sm">{t("ugcStudio.product.startHint")}</div>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {products.map((p) => (
                <ProductCard
                  key={p.id}
                  product={p}
                  selected={selectedId === p.id}
                  onSelect={() => {
                    onSelect(p);
                    onClose();
                  }}
                  onDelete={() => {
                    if (confirm(t("ugcStudio.product.deleteConfirm", { name: p.name })))
                      deleteMut.mutate(p.id);
                  }}
                  onPreview={(src) => setPreviewUrl(src)}
                />
              ))}
            </div>
          )}
        </div>
        {previewUrl && <ImageLightbox src={previewUrl} onClose={() => setPreviewUrl(null)} />}
      </DialogContent>
    </Dialog>
  );
}

function ImageLightbox({ src, onClose }: { src: string; onClose: () => void }) {
  const { t } = useTranslation();
  return (
    <div
      onClick={onClose}
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 backdrop-blur-sm p-6 cursor-zoom-out"
    >
      <button
        type="button"
        onClick={onClose}
        className="absolute top-4 right-4 p-2 rounded-md bg-white/10 text-white hover:bg-white/20"
        aria-label={t("ugcStudio.product.closePreview")}
      >
        <X className="w-5 h-5" />
      </button>
      <img
        src={src}
        alt={t("ugcStudio.product.closePreview")}
        onClick={(e) => e.stopPropagation()}
        className="max-w-full max-h-full object-contain rounded-lg cursor-default"
      />
    </div>
  );
}

function ProductCard({
  product,
  selected,
  onSelect,
  onDelete,
  onPreview,
}: {
  product: UgcProduct;
  selected: boolean;
  onSelect: () => void;
  onDelete: () => void;
  onPreview: (src: string) => void;
}) {
  const { t } = useTranslation();
  const thumb = product.imageUrls?.[0];
  return (
    <div
      onClick={onSelect}
      className={`group relative rounded-lg overflow-hidden border cursor-pointer transition ${
        selected
          ? "border-lime-400 ring-2 ring-lime-400/30"
          : "border-white/[0.08] hover:border-white/20"
      }`}
    >
      <div className="relative aspect-[4/5] bg-white/[0.03]">
        {thumb ? (
          <>
            <img src={thumb} alt={product.name} className="w-full h-full object-contain" />
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onPreview(thumb);
              }}
              className="absolute bottom-1 left-1 p-1.5 rounded-md bg-black/60 text-white/90 opacity-0 group-hover:opacity-100 transition hover:bg-black/80"
              aria-label={t("ugcStudio.product.enlargeButton")}
              title={t("ugcStudio.product.enlargeButton")}
            >
              <Maximize2 className="w-3 h-3" />
            </button>
          </>
        ) : (
          <div className="w-full h-full flex items-center justify-center text-white/30 text-xs">
            {t("ugcStudio.product.noPhotos")}
          </div>
        )}
      </div>
      <div className="p-2 bg-white/[0.02]">
        <div
          className="text-white text-xs font-medium line-clamp-2 leading-snug"
          title={product.name}
        >
          {product.name}
        </div>
      </div>
      <button
        onClick={(e) => {
          e.stopPropagation();
          onDelete();
        }}
        className="absolute top-1 right-1 p-1.5 rounded-md bg-black/60 text-red-300 opacity-0 group-hover:opacity-100 transition"
      >
        <Trash2 className="w-3 h-3" />
      </button>
    </div>
  );
}

function PlatformPill({
  active,
  onClick,
  label,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-3 py-1.5 rounded-full text-xs font-medium transition border ${
        active
          ? "bg-white text-black border-white shadow-sm"
          : "bg-white/[0.03] text-white/70 border-white/[0.08] hover:text-white hover:border-white/20"
      }`}
    >
      {label}
    </button>
  );
}
